import { useState } from 'react';
import { apiFetch, useApi } from '../hooks/useApi.js';
import { uiConfirm, uiAlert } from '../utils/dialogs.js';

// -- Shared UI primitives --
function Btn({ children, variant = 'primary', size = 'md', ...props }) {
  const base = { borderRadius: 'var(--radius)', fontWeight: 600, cursor: 'pointer', border: 'none', fontSize: size === 'sm' ? 12 : 14, padding: size === 'sm' ? '5px 12px' : '9px 18px', opacity: props.disabled ? 0.5 : 1, transition: 'opacity .15s' };
  const v = { primary: { background: 'var(--pld-green)', color: '#fff' }, ghost: { background: 'var(--surface-2)', color: 'var(--text)', border: '1px solid var(--border)' }, danger: { background: '#dc2626', color: '#fff' } };
  return <button {...props} style={{ ...base, ...v[variant], ...props.style }}>{children}</button>;
}

function Modal({ title, onClose, width = 560, children }) {
  return (
    <div style={{ position: 'fixed', inset: 0, zIndex: 300, background: 'rgba(0,0,0,.45)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24 }} onClick={onClose}>
      <div style={{ background: 'var(--surface)', borderRadius: 'var(--radius-lg)', boxShadow: 'var(--shadow-lg)', width: '100%', maxWidth: width, maxHeight: '90vh', overflow: 'auto' }} onClick={e => e.stopPropagation()}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '18px 24px', borderBottom: '1px solid var(--border)' }}>
          <h3 style={{ margin: 0, fontSize: 16, fontWeight: 700, color: 'var(--text)' }}>{title}</h3>
          <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 20, color: 'var(--text-muted)', lineHeight: 1 }}>x</button>
        </div>
        <div style={{ padding: 24 }}>{children}</div>
      </div>
    </div>
  );
}

function Field({ label, hint, children }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
      <label style={{ fontSize: 13, fontWeight: 500, color: 'var(--text)' }}>{label}</label>
      {children}
      {hint && <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>{hint}</span>}
    </div>
  );
}

function Input(props) {
  return <input {...props} style={{ padding: '8px 11px', borderRadius: 'var(--radius)', border: '1px solid var(--border)', fontSize: 14, background: 'var(--surface-2)', width: '100%', boxSizing: 'border-box', color: 'var(--text)', ...props.style }} />;
}

function SelectEl({ children, ...props }) {
  return <select {...props} style={{ padding: '8px 11px', borderRadius: 'var(--radius)', border: '1px solid var(--border)', fontSize: 14, background: 'var(--surface-2)', width: '100%', boxSizing: 'border-box', color: 'var(--text)', ...props.style }}>{children}</select>;
}

function Textarea(props) {
  return <textarea {...props} style={{ padding: '8px 11px', borderRadius: 'var(--radius)', border: '1px solid var(--border)', fontSize: 14, background: 'var(--surface-2)', width: '100%', boxSizing: 'border-box', color: 'var(--text)', resize: 'vertical', minHeight: 72, ...props.style }} />;
}

function SectionHeader({ label, action }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
      <h4 style={{ margin: 0, fontSize: 13, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.06em', color: 'var(--text-muted)' }}>{label}</h4>
      {action}
    </div>
  );
}

function TabBar({ tabs, active, onChange }) {
  return (
    <div style={{ display: 'flex', gap: 2, borderBottom: '2px solid var(--border)', marginBottom: 20 }}>
      {tabs.map(t => (
        <button key={t} onClick={() => onChange(t)} style={{ padding: '7px 16px', background: 'none', border: 'none', cursor: 'pointer', fontSize: 13, fontWeight: 600, color: active === t ? 'var(--pld-green)' : 'var(--text-muted)', borderBottom: active === t ? '2px solid var(--pld-green)' : '2px solid transparent', marginBottom: -2 }}>
          {t}
        </button>
      ))}
    </div>
  );
}

// -- Helpers --
const fmtDate = s => !s ? '-' : new Date(s).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
const fmtType = t => t ? t.charAt(0).toUpperCase() + t.slice(1) : '-';
const fmtDelivery = d => ({ DPD: 'DPD', PALLET_HAULAGE: 'Pallet Haulage', CLIENT_COLLECT: 'Client Collect', IN_HOUSE: 'In-House' })[d] ?? d ?? '-';

const OUTLET_TYPES = ['pub', 'restaurant', 'hotel', 'retailer', 'online', 'wholesale', 'distillery', 'shopify', 'other'];
const DELIVERY_METHODS = ['DPD', 'PALLET_HAULAGE', 'CLIENT_COLLECT', 'IN_HOUSE'];

const TYPE_COLOURS = {
  pub:        { bg: '#fef9c3', text: '#854d0e' },
  restaurant: { bg: '#fce7f3', text: '#9d174d' },
  hotel:      { bg: '#ede9fe', text: '#5b21b6' },
  retailer:   { bg: '#dbeafe', text: '#1e40af' },
  online:     { bg: '#dcfce7', text: '#166534' },
  wholesale:  { bg: '#ffedd5', text: '#9a3412' },
  distillery: { bg: '#e0f2fe', text: '#075985' },
  shopify:    { bg: '#f0fdf4', text: '#14532d' },
  other:      { bg: '#f3f4f6', text: '#6b7280' },
};

function TypeBadge({ type }) {
  const c = TYPE_COLOURS[type] ?? TYPE_COLOURS.other;
  return <span style={{ display: 'inline-block', padding: '3px 9px', borderRadius: 99, fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.04em', background: c.bg, color: c.text }}>{fmtType(type)}</span>;
}

function RecallBadge({ verified }) {
  return <span style={{ display: 'inline-block', padding: '3px 9px', borderRadius: 99, fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.04em', background: verified ? '#dcfce7' : '#fee2e2', color: verified ? '#166534' : '#991b1b' }}>{verified ? 'Verified' : 'Unverified'}</span>;
}

// -- Outlet Form Modal --
const BLANK = {
  name: '', account_ref: '', outlet_type: 'pub',
  address_line1: '', address_line2: '', town: '', county: '', postcode: '', country: 'United Kingdom',
  primary_contact_name: '', primary_contact_email: '', primary_contact_phone: '',
  recall_contact_name: '', recall_contact_email: '', recall_contact_phone: '',
  default_delivery_method: '', dpd_account_ref: '',
  xero_contact_id: '', hubspot_company_id: '',
  price_list_id: '',
};

function OutletFormModal({ title, initial = BLANK, onClose, onSaved }) {
  const [form, setForm] = useState({ ...BLANK, ...initial, price_list_id: initial.price_list_id || '' });
  const { data: plData } = useApi('/price-lists');
  const priceLists = (plData?.price_lists ?? []).filter(l => l.status !== 'archived');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  async function handleSubmit(e) {
    e.preventDefault();
    if (!form.name.trim()) { setError('Name is required'); return; }
    setSaving(true); setError('');
    try {
      const isEdit = !!initial.id;
      const outlet = await apiFetch(
        isEdit ? `/own-brand/outlets/${initial.id}` : '/own-brand/outlets',
        { method: isEdit ? 'PATCH' : 'POST', body: { ...form, price_list_id: form.price_list_id || null } }
      );
      onSaved(outlet);
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  }

  return (
    <Modal title={title} onClose={onClose} width={680}>
      <form onSubmit={handleSubmit}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>

          <div>
            <SectionHeader label="Outlet" />
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
              <div style={{ gridColumn: '1 / -1' }}>
                <Field label="Name *">
                  <Input value={form.name} onChange={e => set('name', e.target.value)} placeholder="The Crown" required />
                </Field>
              </div>
              <Field label="Account Ref">
                <Input value={form.account_ref} onChange={e => set('account_ref', e.target.value)} placeholder="ACC-001" />
              </Field>
              <Field label="Type">
                <SelectEl value={form.outlet_type} onChange={e => set('outlet_type', e.target.value)}>
                  {OUTLET_TYPES.map(t => <option key={t} value={t}>{fmtType(t)}</option>)}
                </SelectEl>
              </Field>
            </div>
          </div>

          <div>
            <SectionHeader label="Address" />
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
              <div style={{ gridColumn: '1 / -1' }}>
                <Field label="Line 1"><Input value={form.address_line1} onChange={e => set('address_line1', e.target.value)} /></Field>
              </div>
              <div style={{ gridColumn: '1 / -1' }}>
                <Field label="Line 2"><Input value={form.address_line2} onChange={e => set('address_line2', e.target.value)} /></Field>
              </div>
              <Field label="Town"><Input value={form.town} onChange={e => set('town', e.target.value)} /></Field>
              <Field label="County"><Input value={form.county} onChange={e => set('county', e.target.value)} /></Field>
              <Field label="Postcode"><Input value={form.postcode} onChange={e => set('postcode', e.target.value.toUpperCase())} /></Field>
              <Field label="Country"><Input value={form.country} onChange={e => set('country', e.target.value)} /></Field>
            </div>
          </div>

          <div>
            <SectionHeader label="Primary Contact" />
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
              <div style={{ gridColumn: '1 / -1' }}>
                <Field label="Name"><Input value={form.primary_contact_name} onChange={e => set('primary_contact_name', e.target.value)} /></Field>
              </div>
              <Field label="Email"><Input type="email" value={form.primary_contact_email} onChange={e => set('primary_contact_email', e.target.value)} /></Field>
              <Field label="Phone"><Input value={form.primary_contact_phone} onChange={e => set('primary_contact_phone', e.target.value)} /></Field>
            </div>
          </div>

          <div>
            <SectionHeader label="Recall Contact" />
            <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
              <Field label="Name"><Input value={form.recall_contact_name} onChange={e => set('recall_contact_name', e.target.value)} /></Field>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
                <Field label="Email"><Input type="email" value={form.recall_contact_email} onChange={e => set('recall_contact_email', e.target.value)} /></Field>
                <Field label="Phone"><Input value={form.recall_contact_phone} onChange={e => set('recall_contact_phone', e.target.value)} /></Field>
              </div>
            </div>
          </div>

          <div>
            <SectionHeader label="Delivery" />
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
              <Field label="Default Method">
                <SelectEl value={form.default_delivery_method} onChange={e => set('default_delivery_method', e.target.value)}>
                  <option value="">- select -</option>
                  {DELIVERY_METHODS.map(m => <option key={m} value={m}>{fmtDelivery(m)}</option>)}
                </SelectEl>
              </Field>
              <Field label="DPD Account Ref">
                <Input value={form.dpd_account_ref} onChange={e => set('dpd_account_ref', e.target.value)} placeholder="Only needed for DPD" />
              </Field>
              <Field label="Price list (sales orders fill in prices from it)">
                <SelectEl value={form.price_list_id} onChange={e => set('price_list_id', e.target.value)}>
                  <option value="">- none -</option>
                  {priceLists.map(l => <option key={l.id} value={l.id}>{l.name}{l.line_count ? ` (${l.line_count} products)` : ''}</option>)}
                </SelectEl>
              </Field>
            </div>
          </div>

          <div>
            <SectionHeader label="Integrations" />
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
              <Field label="Xero Contact ID"><Input value={form.xero_contact_id} onChange={e => set('xero_contact_id', e.target.value)} /></Field>
              <Field label="HubSpot Company ID"><Input value={form.hubspot_company_id} onChange={e => set('hubspot_company_id', e.target.value)} /></Field>
            </div>
          </div>

          {error && <p style={{ margin: 0, color: '#dc2626', fontSize: 13 }}>{error}</p>}

          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10 }}>
            <Btn type="button" variant="ghost" onClick={onClose}>Cancel</Btn>
            <Btn type="submit" disabled={saving}>{saving ? 'Saving...' : (initial.id ? 'Save Changes' : 'Create Outlet')}</Btn>
          </div>
        </div>
      </form>
    </Modal>
  );
}

// -- Outlet Detail --
function OutletDetail({ outletId, onBack }) {
  const { data: outlet, loading, error, refetch } = useApi(`/own-brand/outlets/${outletId}`, [outletId]);
  const { data: plData } = useApi('/price-lists');
  const priceListName = (plData?.price_lists ?? []).find(l => l.id === outlet?.price_list_id)?.name || '—';
  const [showEdit, setShowEdit] = useState(false);
  const [verifying, setVerifying] = useState(false);
  const [deactivating, setDeactivating] = useState(false);
  const [actionError, setActionError] = useState('');

  async function verifyRecall() {
    setVerifying(true); setActionError('');
    try {
      await apiFetch(`/own-brand/outlets/${outletId}/verify-recall`, { method: 'POST' });
      refetch();
    } catch (err) { setActionError(err.message); }
    finally { setVerifying(false); }
  }

  async function deactivate() {
    if (!(await uiConfirm('Deactivate this outlet? It will be hidden from active lists.'))) return;
    setDeactivating(true); setActionError('');
    try {
      await apiFetch(`/own-brand/outlets/${outletId}`, { method: 'DELETE' });
      onBack();
    } catch (err) { setActionError(err.message); }
    finally { setDeactivating(false); }
  }

  if (loading) return <p style={{ padding: 40, color: 'var(--text-muted)', fontSize: 14 }}>Loading...</p>;
  if (error)   return <p style={{ padding: 40, color: '#dc2626', fontSize: 14 }}>{error}</p>;
  if (!outlet) return null;

  const Row = ({ label, value }) => (
    <div style={{ display: 'flex', gap: 8, padding: '7px 0', borderBottom: '1px solid var(--border)' }}>
      <span style={{ fontSize: 13, color: 'var(--text-muted)', width: 180, flexShrink: 0 }}>{label}</span>
      <span style={{ fontSize: 13, color: 'var(--text)', wordBreak: 'break-word' }}>{value || '-'}</span>
    </div>
  );

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 24 }}>
        <button
          onClick={onBack}
          style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 20, color: 'var(--text-muted)', lineHeight: 1, padding: '4px 8px' }}
        >
          &larr;
        </button>
        <div style={{ flex: 1 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
            <h2 style={{ margin: 0, fontSize: 20, fontWeight: 700, color: 'var(--text)' }}>{outlet.name}</h2>
            <TypeBadge type={outlet.outlet_type} />
            <RecallBadge verified={outlet.recall_contact_verified} />
            {!outlet.is_active && (
              <span style={{ display: 'inline-block', padding: '3px 9px', borderRadius: 99, fontSize: 11, fontWeight: 700, background: '#f3f4f6', color: '#6b7280' }}>
                INACTIVE
              </span>
            )}
          </div>
          {outlet.account_ref && (
            <p style={{ margin: '4px 0 0', fontSize: 13, color: 'var(--text-muted)' }}>Ref: {outlet.account_ref}</p>
          )}
        </div>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          {!outlet.recall_contact_verified && (
            <Btn size="sm" onClick={verifyRecall} disabled={verifying}>
              {verifying ? 'Verifying...' : 'Verify Recall Contact'}
            </Btn>
          )}
          <Btn size="sm" variant="ghost" onClick={() => setShowEdit(true)}>Edit</Btn>
          {outlet.is_active && (
            <Btn size="sm" variant="danger" onClick={deactivate} disabled={deactivating}>Deactivate</Btn>
          )}
        </div>
      </div>

      {actionError && <p style={{ marginBottom: 16, color: '#dc2626', fontSize: 13 }}>{actionError}</p>}

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 24 }}>
        <div style={{ background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 'var(--radius)', padding: '16px 20px' }}>
          <SectionHeader label="Address" />
          <Row label="Line 1" value={outlet.address_line1} />
          {outlet.address_line2 && <Row label="Line 2" value={outlet.address_line2} />}
          <Row label="Town" value={outlet.town} />
          <Row label="County" value={outlet.county} />
          <Row label="Postcode" value={outlet.postcode} />
          <Row label="Country" value={outlet.country} />
        </div>

        <div style={{ background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 'var(--radius)', padding: '16px 20px' }}>
          <SectionHeader label="Primary Contact" />
          <Row label="Name" value={outlet.primary_contact_name} />
          <Row label="Email" value={outlet.primary_contact_email} />
          <Row label="Phone" value={outlet.primary_contact_phone} />
        </div>

        <div style={{ background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 'var(--radius)', padding: '16px 20px' }}>
          <SectionHeader label="Recall Contact" />
          <Row label="Name" value={outlet.recall_contact_name} />
          <Row label="Email" value={outlet.recall_contact_email} />
          <Row label="Phone" value={outlet.recall_contact_phone} />
          <Row label="Verified" value={outlet.recall_contact_verified ? ('Yes - ' + fmtDate(outlet.recall_contact_verified_at)) : 'No'} />
        </div>

        <div style={{ background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 'var(--radius)', padding: '16px 20px' }}>
          <SectionHeader label="Delivery" />
          <Row label="Default Method" value={fmtDelivery(outlet.default_delivery_method)} />
          <Row label="DPD Account Ref" value={outlet.dpd_account_ref} />
          <Row label="Price list" value={priceListName} />
          <SectionHeader label="Integrations" />
          <Row label="Xero Contact ID" value={outlet.xero_contact_id} />
          <Row label="HubSpot Company ID" value={outlet.hubspot_company_id} />
          <SectionHeader label="Record" />
          <Row label="Created" value={fmtDate(outlet.created_at)} />
          <Row label="Updated" value={fmtDate(outlet.updated_at)} />
        </div>
      </div>

      <p style={{ margin: '20px 0 0', fontSize: 13, color: 'var(--text-muted)' }}>
        Payment terms, how they pay and product discounts are set in <a href="/billing?tab=terms" style={{ color: 'var(--pld-green)', fontWeight: 600 }}>Billing → Customer terms</a>.
      </p>

      {showEdit && (
        <OutletFormModal
          title="Edit Outlet"
          initial={outlet}
          onClose={() => setShowEdit(false)}
          onSaved={() => { setShowEdit(false); refetch(); }}
        />
      )}
    </div>
  );
}

// -- Outlet List --
function OutletList({ onSelectOutlet }) {
  const [search, setSearch] = useState('');
  const [typeFilter, setTypeFilter] = useState('');
  const [showInactive, setShowInactive] = useState(false);
  const [showCreate, setShowCreate] = useState(false);
  const [query, setQuery] = useState({ search: '', type: '', inactive: false });

  // The API pages at 100 by default and this list has no paging, so it
  // stopped around 'G'. Ask for every outlet.
  const params = new URLSearchParams({ is_active: String(!query.inactive), limit: 'all' });
  if (query.search) params.set('search', query.search);
  if (query.type)   params.set('outlet_type', query.type);
  const { data, loading, error, refetch } = useApi(`/own-brand/outlets?${params}`, [query]);

  const outlets = Array.isArray(data?.data) ? data.data : Array.isArray(data) ? data : [];

  function doSearch() {
    setQuery({ search, type: typeFilter, inactive: showInactive });
  }

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 18, flexWrap: 'wrap' }}>
        <Input
          placeholder="Search name or town..."
          value={search}
          onChange={e => setSearch(e.target.value)}
          onKeyDown={e => e.key === 'Enter' && doSearch()}
          style={{ maxWidth: 260 }}
        />
        <SelectEl value={typeFilter} onChange={e => setTypeFilter(e.target.value)} style={{ maxWidth: 160 }}>
          <option value="">All types</option>
          {OUTLET_TYPES.map(t => <option key={t} value={t}>{fmtType(t)}</option>)}
        </SelectEl>
        <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, color: 'var(--text-muted)', cursor: 'pointer' }}>
          <input type="checkbox" checked={showInactive} onChange={e => setShowInactive(e.target.checked)} />
          Show inactive
        </label>
        <Btn size="sm" onClick={doSearch}>Search</Btn>
        <div style={{ flex: 1 }} />
        <Btn size="sm" variant="ghost" onClick={() => {
          const url = window.location.origin + '/outlet-register';
          navigator.clipboard.writeText(url).then(() => alert('Registration link copied to clipboard:\n' + url));
        }}>Copy Registration Link</Btn>
        <Btn size="sm" onClick={() => setShowCreate(true)}>+ New Outlet</Btn>
      </div>

      <div style={{ border: '1px solid var(--border)', borderRadius: 'var(--radius)', overflow: 'hidden' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
          <thead>
            <tr style={{ background: 'var(--surface-2)' }}>
              {['Name', 'Type', 'Town', 'Primary Contact', 'Recall', 'Delivery'].map(h => (
                <th key={h} style={{ padding: '10px 14px', textAlign: 'left', fontWeight: 600, color: 'var(--text-muted)', fontSize: 12, textTransform: 'uppercase', letterSpacing: '.05em', borderBottom: '1px solid var(--border)' }}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr><td colSpan={6} style={{ padding: 32, textAlign: 'center', color: 'var(--text-muted)' }}>Loading...</td></tr>
            )}
            {!loading && error && (
              <tr><td colSpan={6} style={{ padding: 32, textAlign: 'center', color: '#dc2626' }}>{error}</td></tr>
            )}
            {!loading && !error && outlets.length === 0 && (
              <tr><td colSpan={6} style={{ padding: 32, textAlign: 'center', color: 'var(--text-muted)' }}>No outlets found</td></tr>
            )}
            {outlets.map(o => (
              <tr
                key={o.id}
                onClick={() => onSelectOutlet(o.id)}
                style={{ cursor: 'pointer', borderBottom: '1px solid var(--border)', opacity: o.is_active ? 1 : 0.55 }}
                onMouseEnter={e => e.currentTarget.style.background = 'var(--surface-2)'}
                onMouseLeave={e => e.currentTarget.style.background = ''}
              >
                <td style={{ padding: '10px 14px', fontWeight: 600, color: 'var(--text)' }}>
                  {o.name}
                  {o.account_ref && <span style={{ fontWeight: 400, color: 'var(--text-muted)', marginLeft: 6 }}>({o.account_ref})</span>}
                </td>
                <td style={{ padding: '10px 14px' }}><TypeBadge type={o.outlet_type} /></td>
                <td style={{ padding: '10px 14px', color: 'var(--text-muted)' }}>{o.town || '-'}</td>
                <td style={{ padding: '10px 14px', color: 'var(--text-muted)' }}>{o.primary_contact_name || '-'}</td>
                <td style={{ padding: '10px 14px' }}><RecallBadge verified={o.recall_contact_verified} /></td>
                <td style={{ padding: '10px 14px', color: 'var(--text-muted)' }}>{fmtDelivery(o.default_delivery_method)}</td>
              </tr>
            ))}
          </tbody>
        </table>
        {data?.meta && (
          <div style={{ padding: '8px 14px', borderTop: '1px solid var(--border)', fontSize: 12, color: 'var(--text-muted)', background: 'var(--surface-2)' }}>
            {data.meta.total} outlet{data.meta.total !== 1 ? 's' : ''}
          </div>
        )}
      </div>

      {showCreate && (
        <OutletFormModal
          title="New Outlet"
          onClose={() => setShowCreate(false)}
          onSaved={() => { setShowCreate(false); refetch(); }}
        />
      )}
    </div>
  );
}

// -- Registration Review Modal --
function RegistrationModal({ reg, onClose, onDone }) {
  const [notes, setNotes] = useState('');
  const [accountRef, setAccountRef] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [sendWelcome, setSendWelcome] = useState(true);
  const [preview, setPreview] = useState(null);

  async function showPreview() {
    try {
      const r = await apiFetch(`/outlet-registrations/${reg.id}/welcome-preview?account_ref=${encodeURIComponent(accountRef)}`);
      setPreview(r.data ?? r);
    } catch (e) { setError(e.message); }
  }

  async function act(action) {
    setSaving(true); setError('');
    try {
      const r = await apiFetch(`/outlet-registrations/${reg.id}/${action}`, {
        method: 'POST',
        body: { review_notes: notes, account_ref: accountRef, send_welcome: sendWelcome },
      });
      const w = r?.welcome;
      if (action === 'approve' && w) {
        await uiAlert(w.sent ? `Outlet created, and the welcome email has gone to ${w.to.join(' and ')}.` : `Outlet created, but the welcome email didn't go: ${w.error}.`);
      }
      onDone();
    } catch (err) { setError(err.message); }
    finally { setSaving(false); }
  }

  const Row = ({ label, value }) => value ? (
    <div style={{ display: 'flex', gap: 8, padding: '5px 0', borderBottom: '1px solid var(--border)' }}>
      <span style={{ fontSize: 13, color: 'var(--text-muted)', width: 160, flexShrink: 0 }}>{label}</span>
      <span style={{ fontSize: 13, color: 'var(--text)' }}>{value}</span>
    </div>
  ) : null;

  return (
    <Modal title="Review Registration" onClose={onClose} width={600}>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
        <div>
          <SectionHeader label="Submitted By" />
          <Row label="Name" value={reg.submitted_by_name} />
          <Row label="Email" value={reg.submitted_by_email} />
          <Row label="Phone" value={reg.submitted_by_phone} />
          <Row label="Submitted" value={fmtDate(reg.created_at)} />
        </div>
        <div>
          <SectionHeader label="Outlet Details" />
          <Row label="Name" value={reg.name} />
          <Row label="Type" value={fmtType(reg.outlet_type)} />
          <Row label="Address" value={[reg.address_line1, reg.address_line2, reg.town, reg.county, reg.postcode].filter(Boolean).join(', ')} />
          <Row label="Country" value={reg.country} />
        </div>
        <div>
          <SectionHeader label="Contacts" />
          <Row label="Primary Name" value={reg.primary_contact_name} />
          <Row label="Primary Email" value={reg.primary_contact_email} />
          <Row label="Primary Phone" value={reg.primary_contact_phone} />
          <Row label="Recall Name" value={reg.recall_contact_name} />
          <Row label="Recall Email" value={reg.recall_contact_email} />
          <Row label="Recall Phone" value={reg.recall_contact_phone} />
        </div>
        {reg.notes && (
          <div>
            <SectionHeader label="Notes from Submitter" />
            <p style={{ margin: 0, fontSize: 13, color: 'var(--text)', background: 'var(--surface-2)', padding: '10px 14px', borderRadius: 'var(--radius)', border: '1px solid var(--border)' }}>{reg.notes}</p>
          </div>
        )}
        <Field label="Account Ref (if approving)">
          <Input value={accountRef} onChange={e => setAccountRef(e.target.value)} placeholder="ACC-001" />
        </Field>
        <Field label="Review Notes">
          <Textarea value={notes} onChange={e => setNotes(e.target.value)} placeholder="Optional notes..." style={{ minHeight: 60 }} />
        </Field>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', fontSize: 13 }}>
          <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}>
            <input type="checkbox" checked={sendWelcome} onChange={e => setSendWelcome(e.target.checked)} />
            Send the welcome email when approved (how to order, 7-day terms, GoCardless Direct Debit)
          </label>
          <Btn type="button" variant="ghost" size="sm" onClick={() => (preview ? setPreview(null) : showPreview())}>{preview ? 'Hide email' : 'See the email'}</Btn>
        </div>
        {preview && (
          <div style={{ border: '1px solid var(--border)', borderRadius: 'var(--radius)', overflow: 'hidden' }}>
            <div style={{ padding: '8px 12px', fontSize: 12, background: 'var(--surface-2)', borderBottom: '1px solid var(--border)' }}>To: {preview.to.join(', ')}<br />Subject: {preview.subject}</div>
            <iframe title="Welcome email" srcDoc={preview.html} sandbox="" style={{ width: '100%', height: 420, border: 'none', background: '#fff' }} />
          </div>
        )}
        {error && <p style={{ margin: 0, color: '#dc2626', fontSize: 13 }}>{error}</p>}
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10 }}>
          <Btn type="button" variant="ghost" onClick={onClose}>Cancel</Btn>
          <Btn type="button" variant="danger" onClick={() => act('reject')} disabled={saving}>Reject</Btn>
          <Btn type="button" onClick={() => act('approve')} disabled={saving}>
            {saving ? 'Saving...' : 'Approve & Create Outlet'}
          </Btn>
        </div>
      </div>
    </Modal>
  );
}

// -- Registrations Tab --
function RegistrationsTab() {
  const [statusFilter, setStatusFilter] = useState('pending');
  const [selected, setSelected] = useState(null);
  const { data, loading, error, refetch } = useApi(`/outlet-registrations?status=${statusFilter}`, [statusFilter]);
  const regs = Array.isArray(data?.data) ? data.data : [];

  const STATUS_COLOURS = {
    pending:  { bg: '#fef9c3', text: '#854d0e' },
    approved: { bg: '#dcfce7', text: '#166534' },
    rejected: { bg: '#fee2e2', text: '#991b1b' },
  };

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 18 }}>
        <SelectEl value={statusFilter} onChange={e => setStatusFilter(e.target.value)} style={{ maxWidth: 160 }}>
          <option value="pending">Pending</option>
          <option value="approved">Approved</option>
          <option value="rejected">Rejected</option>
          <option value="all">All</option>
        </SelectEl>
        {data?.meta && (
          <span style={{ fontSize: 13, color: 'var(--text-muted)' }}>
            {data.meta.total} registration{data.meta.total !== 1 ? 's' : ''}
          </span>
        )}
      </div>

      <div style={{ border: '1px solid var(--border)', borderRadius: 'var(--radius)', overflow: 'hidden' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
          <thead>
            <tr style={{ background: 'var(--surface-2)' }}>
              {['Outlet Name', 'Type', 'Submitted By', 'Town', 'Status', 'Date'].map(h => (
                <th key={h} style={{ padding: '10px 14px', textAlign: 'left', fontWeight: 600, color: 'var(--text-muted)', fontSize: 12, textTransform: 'uppercase', letterSpacing: '.05em', borderBottom: '1px solid var(--border)' }}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr><td colSpan={6} style={{ padding: 32, textAlign: 'center', color: 'var(--text-muted)' }}>Loading...</td></tr>
            )}
            {!loading && error && (
              <tr><td colSpan={6} style={{ padding: 32, textAlign: 'center', color: '#dc2626' }}>{error}</td></tr>
            )}
            {!loading && !error && regs.length === 0 && (
              <tr><td colSpan={6} style={{ padding: 32, textAlign: 'center', color: 'var(--text-muted)' }}>No registrations</td></tr>
            )}
            {regs.map(r => {
              const c = STATUS_COLOURS[r.status] ?? STATUS_COLOURS.pending;
              const isPending = r.status === 'pending';
              return (
                <tr
                  key={r.id}
                  onClick={() => isPending && setSelected(r)}
                  style={{ cursor: isPending ? 'pointer' : 'default', borderBottom: '1px solid var(--border)' }}
                  onMouseEnter={e => { if (isPending) e.currentTarget.style.background = 'var(--surface-2)'; }}
                  onMouseLeave={e => e.currentTarget.style.background = ''}
                >
                  <td style={{ padding: '10px 14px', fontWeight: 600, color: 'var(--text)' }}>{r.name}</td>
                  <td style={{ padding: '10px 14px' }}><TypeBadge type={r.outlet_type} /></td>
                  <td style={{ padding: '10px 14px', color: 'var(--text-muted)' }}>{r.submitted_by_name}</td>
                  <td style={{ padding: '10px 14px', color: 'var(--text-muted)' }}>{r.town || '-'}</td>
                  <td style={{ padding: '10px 14px' }}>
                    <span style={{ display: 'inline-block', padding: '3px 9px', borderRadius: 99, fontSize: 11, fontWeight: 700, textTransform: 'uppercase', background: c.bg, color: c.text }}>{r.status}</span>
                  </td>
                  <td style={{ padding: '10px 14px', color: 'var(--text-muted)' }}>{fmtDate(r.created_at)}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {selected && (
        <RegistrationModal
          reg={selected}
          onClose={() => setSelected(null)}
          onDone={() => { setSelected(null); refetch(); }}
        />
      )}
    </div>
  );
}

// -- Default export --
export default function Outlets() {
  const [tab, setTab] = useState('Outlets');
  const [selectedId, setSelectedId] = useState(null);

  if (selectedId) {
    return (
      <div style={{ padding: 24 }}>
        <OutletDetail outletId={selectedId} onBack={() => setSelectedId(null)} />
      </div>
    );
  }

  return (
    <div style={{ padding: 24 }}>
      <div style={{ marginBottom: 20 }}>
        <h1 style={{ margin: 0, fontSize: 22, fontWeight: 700, color: 'var(--text)' }}>Outlets</h1>
      </div>
      <TabBar tabs={['Outlets', 'Registrations']} active={tab} onChange={setTab} />
      {tab === 'Outlets'       && <OutletList onSelectOutlet={id => setSelectedId(id)} />}
      {tab === 'Registrations' && <RegistrationsTab />}
    </div>
  );
}
